import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { isEmpty } from 'lodash';

import {
  TipoCardRoot,
  TipoPerfilUsuarioEnum,
  TipoConsultaParametrizacaoEnum,
} from '../enums';

import { getParametrizacaoRoot } from '../utils/treeViewBuilder';
import { getDifferenceBetween } from '../utils/array';
import { isNumber } from '../utils/validation';

import modal from '../context/TriggerModalContext';

import { Creators as UsuarioActions } from '../store/ducks/usuario';
import { Creators as ContratanteActions } from '../store/ducks/contratante';

// #region Componentes
import MessageInvalidCard from '../components/molecules/MessageInvalidCard';
import {
  ModalCopyEspelho,
  ModalMesclarNestle,
  ModalSelectedTipoPerfil,
  ModalRemoveAllParametrizacao,
} from '../components/molecules/PartsModalParametrizacao';
// #endregion

const useParametrizacao = (idUsuario = null, role = null) => {
  const dispatch = useDispatch();
  const {
    getContratantes,
    parametrizacaoRoot,
    getGrupoComplementos,
  } = useSelector((state) => state.contratante);
  const { usuario } = useSelector((state) => state.auth);
  const { patchParametrizacaoInativar, saveParametrizacaoCards } = useSelector(
    (state) => state.usuario
  );

  const paginationModalParams = {
    pageNumber: 1,
    pageSize: 5,
    nextPage: null,
    previousPage: null,
    totalPages: null,
  };

  // #region estados (states)
  const [selectedUser, setSelectedUsuarioEspelho] = useState(null);
  const [paginationParams, setPaginationParams] = useState(
    paginationModalParams
  );
  const [rowsChecked, setRowsChecked] = useState([]);
  const [tableFilterparams, setTableFilterParams] = useState({
    tipoParametrizacao: TipoConsultaParametrizacaoEnum.returnNameFormatted[1],
    buscaParametrizacao: 6,
  });
  const [parametrizacaoFilterParams, setParametrizacaoFilterParams] = useState({
    tipoParametrizacao: TipoConsultaParametrizacaoEnum.returnNameFormatted[0],
    buscaParametrizacao: '',
  });

  const [selectedTipoPerfil, setSelectedTipoPerfil] = useState('');
  // #endregion

  // #region functions privadas
  const hasGrupoNestle = useMemo(() => {
    return parametrizacaoRoot?.some(
      (parametro) =>
        parametro?.tipoParametrizacao ===
          TipoConsultaParametrizacaoEnum.returnNameFormatted[1] &&
        parametro.id === 'NESTLE'
    );
  }, [parametrizacaoRoot]);

  const getName = useCallback((type, selectedCard) => {
    const types = {
      'Grupo ou Complemento': `${selectedCard?.complemento?.codigoComplemento}`,
      Contratante: `${selectedCard?.contratante?.nomeContratante}`,
    };

    return types[type];
  }, []);

  const setParamsBusca = useCallback(
    (tipoBusca, newPageParams) => {
      const { buscaParametrizacao } = tableFilterparams;

      const tipoParamsBusca = {
        [TipoConsultaParametrizacaoEnum.returnName[1]]: {
          codigoGrupo: isNumber(buscaParametrizacao)
            ? buscaParametrizacao
            : null,
          codigoComplemento: isNumber(buscaParametrizacao)
            ? null
            : buscaParametrizacao,
          ...(newPageParams || paginationParams),
        },
        Contratante: {
          nomeContratante: isNumber(buscaParametrizacao)
            ? null
            : buscaParametrizacao,
          codigoContratante: isNumber(buscaParametrizacao)
            ? buscaParametrizacao
            : null,
          ...(newPageParams || paginationParams),
        },
        [TipoConsultaParametrizacaoEnum.returnName[3]]: {
          nome: !/@/gim.test(buscaParametrizacao) ? buscaParametrizacao : null,
          email: /@/gim.test(buscaParametrizacao) ? buscaParametrizacao : null,
          ...(newPageParams || paginationParams),
          has_parametrizacao: true,
        },
      };

      return tipoParamsBusca[tipoBusca];
    },
    [paginationParams, tableFilterparams]
  );

  const getValidsCard = useCallback(() => {
    const POSSUI_PARAMETRIZACAO = parametrizacaoRoot.length > 0;

    // #region Filtrando novos cards
    let cards = rowsChecked.filter(({ id, complemento }) => {
      const IS_CARD_GRUPO = typeof id === 'string';

      if (IS_CARD_GRUPO)
        return !parametrizacaoRoot.some(
          ({ complemento: complemento2 }) =>
            complemento2?.codigoGrupo === complemento?.codigoGrupo
        );

      return !parametrizacaoRoot.some(({ id: id2 }) => id2 === id);
    });
    // #endregion

    const inValidCards = [];
    if (POSSUI_PARAMETRIZACAO)
      cards.forEach((novo) => {
        parametrizacaoRoot.forEach((parametrizado) => {
          parametrizado.listaContratantes.forEach((p) => {
            if (novo.listaContratantes.includes(p)) {
              const invalid = {
                ...novo,
                vinculo: parametrizado,
              };
              inValidCards.push(invalid);
            }
          });
        });
      });

    cards = getDifferenceBetween(cards, 'id', inValidCards, 'id');

    if (inValidCards.length > 0) {
      modal.warning(
        <MessageInvalidCard getName={getName} inValidCards={inValidCards} />
      );
    }

    // #region Mesclar Nestle com outras contratantes
    const POSSUI_GRUPO_NESTLE_E_ESTA_ADICIONANDO_OUTRA_CONTRATANTE =
      hasGrupoNestle &&
      POSSUI_PARAMETRIZACAO &&
      cards.some((card) => card?.id !== 'NESTLE');
    const POSSUI_OUTRA_CONTRATANTE_E_ESTA_ADICIONANDO_GRUPO_NESTLE =
      !hasGrupoNestle &&
      POSSUI_PARAMETRIZACAO &&
      cards.some((card) => card?.id === 'NESTLE');

    if (
      POSSUI_GRUPO_NESTLE_E_ESTA_ADICIONANDO_OUTRA_CONTRATANTE ||
      POSSUI_OUTRA_CONTRATANTE_E_ESTA_ADICIONANDO_GRUPO_NESTLE
    ) {
      modal.warning(<ModalMesclarNestle.BodyMesclarNestle />);
      return [];
    }
    // #endregion

    return cards;
  }, [getName, hasGrupoNestle, parametrizacaoRoot, rowsChecked]);

  const getAndBuildParametrizacaoCardsUsuarioEspelho = useCallback(() => {
    const params = {
      idUsuario: selectedUser?.idUsuario,
      isParametrizacaoEspelho: true,
    };

    dispatch(UsuarioActions.usuarioEspelhoSet(selectedUser));
    dispatch(UsuarioActions.getAndBuildParametrizacaoCardsRequest(params));
  }, [dispatch, selectedUser]);

  const cloneUsuarioEspelho = useCallback(() => {
    dispatch(
      UsuarioActions.postClonarUsuarioEspelhoRequest(
        idUsuario,
        selectedUser.idUsuario
      )
    );
  }, [dispatch, idUsuario, selectedUser]);

  const handleAddUsuarioEspelho = useCallback(() => {
    const handleClickSimModal = (closeModal) => {
      if (idUsuario === 0) {
        getAndBuildParametrizacaoCardsUsuarioEspelho();
      } else {
        cloneUsuarioEspelho();
      }
      setSelectedUsuarioEspelho(null);
      closeModal();
    };

    modal.warning(
      <ModalCopyEspelho.BodyCopyUsuarioEspelho userName={selectedUser.nome} />,
      () => {},
      ({ closeModal }) => (
        <ModalCopyEspelho.FooterCopyUsuarioEspelho
          closeModal={closeModal}
          onClickOk={() => {
            handleClickSimModal(closeModal);
          }}
        />
      )
    );
  }, [
    idUsuario,
    selectedUser,
    cloneUsuarioEspelho,
    getAndBuildParametrizacaoCardsUsuarioEspelho,
  ]);

  const dispatchPatchPerfil = useCallback(
    (newPerfils) => {
      dispatch(
        UsuarioActions.patchAtualizarTipoPerfilRequest(idUsuario, [
          ...newPerfils,
        ])
      );
    },
    [dispatch, idUsuario]
  );

  const showModalSelectedTipoPerfil = useCallback(
    (callbackDispathSaveParametrizacao) => {
      modal.warning(
        <ModalSelectedTipoPerfil.BodySelectedTipoPerfil />,
        () => {},
        ({ closeModal }) => (
          <ModalSelectedTipoPerfil.FooterSelectedTipoPerfil
            handleSelectedTipoPerfil={(tipoPerfil) => {
              setSelectedTipoPerfil(tipoPerfil);
              callbackDispathSaveParametrizacao();
              closeModal();
            }}
          />
        )
      );
    },
    []
  );

  const returnPostAndPutCards = useCallback(() => {
    const postCards = [];
    const putCards = [];

    if (parametrizacaoRoot.length > 0) {
      parametrizacaoRoot.forEach((card) => {
        const parametros = getParametrizacaoRoot(card);
        const parametrizacaoRootCard = parametros.parametrizacaoRoot;

        if (card.idParametrizacao === 0) {
          postCards.push({
            name: getName(card.tipoParametrizacao, card),
            idParametrizacao: 0,
            idCard: card.idCard,
            idUsuarioInclusao: usuario.idUsuarioLogado,
            tipoParametrizacao:
              TipoCardRoot.returnNameForName[card.tipoParametrizacao],
            parametrizacaoRoot: parametrizacaoRootCard,
          });
        } else {
          putCards.push({
            name: getName(card.tipoParametrizacao, card),
            idParametrizacao: card.idParametrizacao,
            idCard: card.idCard,
            idUsuarioInclusao: usuario.idUsuarioLogado,
            tipoParametrizacao:
              TipoCardRoot.returnNameForName[card.tipoParametrizacao],
            parametrizacaoRoot: parametrizacaoRootCard,
          });
        }
      });
    }

    return { postCards, putCards };
  }, [getName, parametrizacaoRoot, usuario]);
  // #endregion

  // #region functions publicas
  const handleRemoveAllCards = useCallback(() => {
    modal.warning(
      <ModalRemoveAllParametrizacao.BodyRemoveAllParametrizacao />,
      () => {},
      ({ closeModal }) => (
        <ModalRemoveAllParametrizacao.FooterRemoveAllParametrizacao
          closeModal={closeModal}
          onClickOk={() => {
            dispatch(
              UsuarioActions.patchParametrizacaoAdmInativarRequest(idUsuario)
            );
            closeModal();
          }}
        />
      )
    );
  }, [dispatch, idUsuario]);

  const handleRemoveCard = useCallback(
    (idCard, idParametrizacao) => {
      const ULTIMO_CARD = parametrizacaoRoot.length === 1;
      const IS_ADM = role === TipoPerfilUsuarioEnum.returnNameForName.Adm;

      if (idParametrizacao > 0) {
        dispatch(
          UsuarioActions.patchParametrizacaoInativarRequest(
            idUsuario,
            idCard,
            idParametrizacao
          )
        );
        if (ULTIMO_CARD && IS_ADM) {
          // Removendo perfil de contratante quando remover todas as parametrizações do adm
          dispatchPatchPerfil([TipoPerfilUsuarioEnum.returnNameForName.Adm]);
        }
      } else {
        dispatch(ContratanteActions.parametrizacaoDeleteCard(idCard));
      }
    },
    [role, dispatch, idUsuario, dispatchPatchPerfil, parametrizacaoRoot.length]
  );

  const hasAlteracao = useMemo(() => {
    const { postCards } = returnPostAndPutCards();
    return postCards.length > 0;
  }, [returnPostAndPutCards]);

  const handleAdicionarModal = useCallback(() => {
    // #region Parametrização de usuario espelho
    const ESTA_NA_ABA_USUARIO_ESPELHO =
      tableFilterparams.tipoParametrizacao ===
      TipoConsultaParametrizacaoEnum.returnName[3];

    if (selectedUser && selectedUser.idUsuario && ESTA_NA_ABA_USUARIO_ESPELHO) {
      handleAddUsuarioEspelho();
      return;
    }
    // #endregion

    // #region Parametrização normal (contratante/grupo)
    const contratanteRoot = [];
    const grupoRoot = [];

    const cards = getValidsCard();

    if (!isEmpty(cards)) {
      const newCards = cards.map((card) => {
        if (card.tipoParametrizacao === TipoCardRoot.returnName[0]) {
          contratanteRoot.push(card);
        }
        if (card.tipoParametrizacao === TipoCardRoot.returnName[1]) {
          grupoRoot.push(card);
        }

        const newCard = { ...card };

        return newCard;
      });

      batch(() => {
        dispatch(ContratanteActions.parametrizacaoSetCards(newCards));
        if (contratanteRoot.length > 0)
          dispatch(
            ContratanteActions.getParametrizacaoContratanteCompletoRequest(
              contratanteRoot
            )
          );
        if (grupoRoot.length > 0) {
          dispatch(
            ContratanteActions.getParametrizacaoGrupoComplementoCompletoRequest(
              grupoRoot
            )
          );
        }
      });
    }
    // #endregion
  }, [
    dispatch,
    selectedUser,
    getValidsCard,
    tableFilterparams,
    handleAddUsuarioEspelho,
  ]);

  const cleanFilterParams = useCallback(() => {
    setTableFilterParams({
      tipoParametrizacao: TipoConsultaParametrizacaoEnum.returnNameFormatted[1],
      buscaParametrizacao: '',
    });
    setRowsChecked([]);
  }, []);

  const setTipoBusca = useCallback(
    (tipoBusca, newPageParams) => {
      const paramsDispatch = setParamsBusca(tipoBusca, newPageParams);

      const tiposBusca = {
        [TipoConsultaParametrizacaoEnum
          .returnName[1]]: ContratanteActions.getGrupoComplementosRequest(
          paramsDispatch
        ),
        Contratante: ContratanteActions.getContratanteRequest(paramsDispatch),
        [TipoConsultaParametrizacaoEnum
          .returnName[3]]: UsuarioActions.getUsuarioEspelhoRequest(
          paramsDispatch
        ),
      };

      return tiposBusca[tipoBusca];
    },
    [setParamsBusca]
  );

  const salvarParametrizacao = useCallback(() => {
    const { postCards, putCards } = returnPostAndPutCards();

    if (hasAlteracao) {
      const callbackDispathSaveParametrizacao = () => {
        dispatch(
          UsuarioActions.saveParametrizacaoCardsRequest(
            idUsuario,
            postCards,
            putCards,
            true
          )
        );
      };

      if (role === TipoPerfilUsuarioEnum.returnNameForName.Adm) {
        showModalSelectedTipoPerfil(callbackDispathSaveParametrizacao);
        return;
      }

      callbackDispathSaveParametrizacao();
    }
  }, [
    role,
    dispatch,
    idUsuario,
    hasAlteracao,
    returnPostAndPutCards,
    showModalSelectedTipoPerfil,
  ]);

  const handleCheckUser = useCallback(
    (newUser) => {
      setSelectedUsuarioEspelho((oldUser) =>
        oldUser?.idUsuario === newUser?.idUsuario ? null : newUser
      );
    },
    [setSelectedUsuarioEspelho]
  );

  const handleOnClickSalvarCard = useCallback(
    (selectedCard) => {
      const postCard = [];
      const putCard = [];
      if (selectedCard.idParametrizacao === 0) {
        const {
          parametrizacaoRoot: parametrizacaoRootProp,
        } = getParametrizacaoRoot(selectedCard);

        postCard.push({
          name: getName(selectedCard.tipoParametrizacao, selectedCard),
          idParametrizacao: 0,
          idCard: selectedCard.idCard,
          idUsuarioInclusao: usuario.idUsuarioLogado,
          tipoParametrizacao:
            TipoCardRoot.returnNameForName[selectedCard.tipoParametrizacao],
          parametrizacaoRoot: parametrizacaoRootProp,
        });
      } else {
        const {
          parametrizacaoRoot: parametrizacaoRootProp,
        } = getParametrizacaoRoot(selectedCard);
        putCard.push({
          name: getName(selectedCard),
          idParametrizacao: selectedCard.idParametrizacao,
          idCard: selectedCard.idCard,
          idUsuarioInclusao: usuario.idUsuarioLogado,
          tipoParametrizacao:
            TipoCardRoot.returnNameForName[selectedCard.tipoParametrizacao],
          parametrizacaoRoot: parametrizacaoRootProp,
        });
      }

      const callbackDispathSaveParametrizacao = () => {
        batch(() => {
          dispatch(
            UsuarioActions.saveParametrizacaoCardsRequest(
              idUsuario,
              postCard,
              putCard,
              false
            )
          );
          dispatch(
            ContratanteActions.parametrizacaoToogleCard([
              { idCard: selectedCard.idCard },
            ])
          );
        });
      };

      if (role === TipoPerfilUsuarioEnum.returnNameForName.Adm) {
        showModalSelectedTipoPerfil(callbackDispathSaveParametrizacao);
        return;
      }

      callbackDispathSaveParametrizacao();
    },
    [dispatch, getName, idUsuario, role, showModalSelectedTipoPerfil, usuario]
  );
  // #endregion

  // #region ciclo de vida
  useEffect(() => {
    const USUARIO_PARAMETRIZACAO_E_USUARIO_LOGADO =
      idUsuario === usuario?.idUsuarioLogado;

    if (
      USUARIO_PARAMETRIZACAO_E_USUARIO_LOGADO &&
      patchParametrizacaoInativar?.success
    ) {
      // Caso a parametrização seja do usuario logado, deve-se atualizar o drop de contratantes
      dispatch(
        UsuarioActions.getContratantesUsuarioRequest(usuario.idUsuarioLogado)
      );
    }
  }, [
    dispatch,
    idUsuario,
    usuario.idUsuarioLogado,
    patchParametrizacaoInativar,
  ]);

  useEffect(() => {
    const IS_ADM = role === TipoPerfilUsuarioEnum.returnNameForName.Adm;

    if (IS_ADM && saveParametrizacaoCards?.success && selectedTipoPerfil) {
      dispatchPatchPerfil([
        TipoPerfilUsuarioEnum.returnNameForName.Adm,
        selectedTipoPerfil,
      ]);
      setSelectedTipoPerfil('');
    }
  }, [
    role,
    selectedTipoPerfil,
    dispatchPatchPerfil,
    saveParametrizacaoCards?.success,
  ]);
  // #endregion

  return {
    selectedUser,
    handleCheckUser,
    parametrizacaoRoot,
    handleAdicionarModal,
    handleOnClickSalvarCard,

    handleRemoveAllCards,
    handleRemoveCard,
    rowsChecked,
    hasAlteracao,
    setTipoBusca,
    setRowsChecked,
    getContratantes,
    paginationParams,
    cleanFilterParams,
    tableFilterparams,
    setPaginationParams,
    getGrupoComplementos,
    setTableFilterParams,
    salvarParametrizacao,
    paginationModalParams,
    parametrizacaoFilterParams,
    setParametrizacaoFilterParams,
  };
};

export default useParametrizacao;
